var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useState, } from "react";
import Draggable from "react-draggable";
import "../FloatingToolbar.css";
import { ExpandCollapseIcon } from "./InternalIcon";
import classNames from "classnames";
import { useGetPosition } from "../hook/useGetPosition";
import { Toolbar } from "../../toolbar";
import { useUuid } from "../../common/hooks/useUuid";
import { useWindowEventListener } from "../../common/hooks/useWindowEventListener";
import { useGetElementSize } from "../hook/useGetElementSize";
import { TOOLBAR_BORDER_WIDTH } from "../constants";
import { useGetInitPosition } from "../hook/useGetInitPosition";
import { NotificationList, } from "../../notification-list/component/NotificationList";
import { useToolbarCollapseStore } from "../hook/useToolbarCollapseStore";
import { useToolbarMetricsStore } from "../hook/useToolbarMetricsStore";
import { useToolbarTutorialStore } from "../hook/useToolbarTutorialStore";
import Hotspot from "@cloudscape-design/components/hotspot";
import { TOOLBAR_ITEM_WIDTH } from "../../toolbar/constants";
import { useToolbarTimer } from "../hook/useToolbarTimer";
/**
 * This component renders a floating component containing a list of toolbar buttons.
 * The toolbar can be collapsed or expanded. When collapsed, only the `minimizedIcon`
 * and `minimizedText` will be rendered. The toolbar items can be either stateful or
 * stateless.
 * - Stateless: Do not have an expanded state and do not change appearance on click
 * - Stateful
 *     - Headless: Do not have an expanded state but will change appearance on click
 *     - Container: Have an expanded container content below the toolbar and will
 *   change appearance on click
 *
 * Tutorial with hotspot
 * WithHotspot is used for tutorial purpose and should be used together with TutorialAnnotationContext.
 * When withHotspot is true, tutorial will automatically be shown on start and hidden when user finishes the tutorial.
 * If toolbar is collapsed, tutorial will be hidden. If toolbar is expanded and tutorial is not completed, tutorial will be shown again.
 */
export var FloatingToolbar = function (_a) {
    var _b, _c, _d, _e, _f;
    var _g = _a.bounds, bounds = _g === void 0 ? "body" : _g, _h = _a.magneticOffset, magneticOffset = _h === void 0 ? 30 : _h, _j = _a.withHotspot, withHotspot = _j === void 0 ? false : _j, props = __rest(_a, ["bounds", "magneticOffset", "withHotspot"]);
    var uuid = useUuid([]);
    var handleId = useMemo(function () { return "floating-toolbar-handle-".concat(uuid); }, [uuid]);
    var _k = useState(false), dragging = _k[0], setDragging = _k[1];
    var _l = useToolbarCollapseStore(), collapsed = _l.collapsed, setCollapsed = _l.setCollapsed;
    useToolbarTimer(collapsed);
    var _m = useToolbarMetricsStore(), addMovementCount = _m.addMovementCount, addExpandButtonClickCount = _m.addExpandButtonClickCount, addExpandHandleClickCount = _m.addExpandHandleClickCount, addCollapseButtonClickCount = _m.addCollapseButtonClickCount, addCollapseHandleClickCount = _m.addCollapseHandleClickCount;
    var _o = useToolbarTutorialStore(), showTutorial = _o.showTutorial, setShowTutorial = _o.setShowTutorial, isTutorialCompleted = _o.isTutorialCompleted, setTutorialCompleted = _o.setTutorialCompleted;
    var floatingNotificationItems = (_c = (_b = props.floatingNotificationListProps) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : [];
    var _p = useGetElementSize([
        props.items.length,
    ]), toolbarContentRef = _p[0], maximizedSize = _p[1];
    var _q = useGetElementSize([props.minimizedView]), minimizedHandleElRef = _q[0], minimizedSize = _q[1];
    // Make sure this constant value is consistent with --tutorial-padding-left in css file.
    var toolbarLeftPadding = 350;
    useEffect(function () {
        if (!withHotspot) {
            setShowTutorial(false);
            setTutorialCompleted(true);
        }
    }, [withHotspot]);
    var _r = useGetPosition({
        collapsed: collapsed,
        boundSelector: bounds,
        magneticOffset: magneticOffset,
        min: { x: 0, y: 0 },
    }), observe = _r[0], position = _r[1], size = _r[2], snappedBottom = _r[3], setPosition = _r[4], setCalcPosition = _r[5], forceUpdatePosition = _r[6];
    // Floating notifications will be hidden in the minimized mode
    var showFloatingNotification = floatingNotificationItems.length > 0 && !collapsed;
    var toolbarOnBottom = snappedBottom && !showFloatingNotification;
    // initPosition will be set only once, when we know the toolbar width and the bounds element is ready
    var initPosition = useGetInitPosition(bounds, maximizedSize, props.defaultPosition);
    useEffect(function () {
        initPosition &&
            setCalcPosition(showTutorial ? initPosition.x - toolbarLeftPadding : initPosition.x, initPosition.y);
    }, [initPosition === null || initPosition === void 0 ? void 0 : initPosition.x, initPosition === null || initPosition === void 0 ? void 0 : initPosition.y]);
    useWindowEventListener("resize", function () {
        /**
         * To avoid destroying/creating new event listeners too often,
         * we will call `forceUpdatePosition` instead.
         */
        forceUpdatePosition();
    });
    useLayoutEffect(function () {
        // On toolbar size change
        setCalcPosition(position.x, position.y);
    }, [size.width, size.height]);
    useLayoutEffect(function () {
        // Adjust position based on tutorial visibility since we add padding dynamically
        if (withHotspot) {
            if (!showTutorial) {
                setCalcPosition(position.x + toolbarLeftPadding, position.y);
            }
            else {
                setCalcPosition(position.x - toolbarLeftPadding, position.y);
            }
        }
    }, [showTutorial]);
    useLayoutEffect(function () {
        setPositionOnMaximizeMinimize(!collapsed);
        // Don't show tutorial when the toolbar is collapsed and show tutorial when it's expanded and not completed before
        if (collapsed) {
            setShowTutorial(false);
        }
        else if (!isTutorialCompleted && withHotspot) {
            setShowTutorial(true);
        }
    }, [collapsed]);
    /**
     * Change toolbar position to expand from/collapse to center.
     */
    var setPositionOnMaximizeMinimize = function (toExpand) {
        if (minimizedSize && maximizedSize) {
            setPosition(position.x +
                (toExpand
                    ? minimizedSize.width / 2 - maximizedSize.width / 2 // Expanding toolbar
                    : maximizedSize.width / 2 - minimizedSize.width / 2), // Collapsing toolbar
            position.y);
        }
    };
    return (_jsxs(_Fragment, { children: [withHotspot && (_jsx("div", __assign({ className: "hotspot3" }, { children: _jsx(Hotspot, { direction: "bottom", hotspotId: "hotspot3" }) }))), _jsx(Draggable, __assign({ bounds: bounds, handle: "#".concat(handleId), onStart: function () {
                    setDragging(true);
                }, onDrag: function (e, ui) {
                    setPosition(ui.x, ui.y);
                }, onStop: function (e, ui) {
                    setDragging(false);
                    setCalcPosition(ui.x, ui.y);
                    addMovementCount();
                }, position: position }, { children: _jsxs("div", __assign({ ref: observe, className: classNames("floating_toolbar_wrapper", {
                        tutorial: withHotspot && showTutorial,
                        dragging: dragging,
                        "show-floating-notification": showFloatingNotification,
                        bottom: toolbarOnBottom,
                    }), style: { maxWidth: "".concat(props.items.length * TOOLBAR_ITEM_WIDTH, "px") } }, { children: [withHotspot && (_jsx("div", __assign({ className: "hotspot1" }, { children: _jsx(Hotspot, { direction: "left", hotspotId: "hotspot1" }) }))), _jsxs("div", __assign({ className: "floating_toolbar", style: { borderWidth: "".concat(TOOLBAR_BORDER_WIDTH, "px") }, ref: toolbarContentRef }, { children: [_jsx("div", { id: handleId, className: classNames("floating_handle", "prevent-select"), onDoubleClick: function () {
                                        if (collapsed) {
                                            addExpandHandleClickCount();
                                        }
                                        else {
                                            addCollapseHandleClickCount();
                                        }
                                        setCollapsed(!collapsed);
                                    } }), _jsx("div", __assign({ className: classNames("minimized-view-wrapper", "prevent-select", {
                                        collapsed: collapsed,
                                    }) }, { children: _jsx("div", __assign({ ref: minimizedHandleElRef, className: "minimized-view" }, { children: props.minimizedView })) })), _jsx("div", __assign({ className: classNames("toolbar-wrapper", {
                                        collapsed: collapsed,
                                    }) }, { children: _jsx(Toolbar, { i18nStrings: props.i18nStrings, items: props.items, isCollapsed: collapsed, isFloating: true }) }))] })), withHotspot && (_jsxs(_Fragment, { children: [_jsx("div", __assign({ className: "hotspot2" }, { children: _jsx(Hotspot, { direction: "bottom", hotspotId: "hotspot2" }) })), _jsx("div", __assign({ className: "hotspot4" }, { children: _jsx(Hotspot, { direction: "bottom", hotspotId: "hotspot4" }) }))] })), _jsx("div", __assign({ className: classNames("toggle_expand_collapse", "prevent-select") }, { children: _jsx("button", __assign({ title: collapsed
                                    ? (_d = props.i18nStrings.expandButtonTitle) !== null && _d !== void 0 ? _d : props.i18nStrings.expandButtonAriaLabel
                                    : (_e = props.i18nStrings.collapseButtonTitle) !== null && _e !== void 0 ? _e : props.i18nStrings.collapseButtonAriaLabel, "aria-label": collapsed
                                    ? props.i18nStrings.expandButtonAriaLabel
                                    : props.i18nStrings.collapseButtonAriaLabel, "aria-expanded": !collapsed, onClick: function () {
                                    if (collapsed) {
                                        addExpandButtonClickCount();
                                    }
                                    else {
                                        addCollapseButtonClickCount();
                                    }
                                    setCollapsed(!collapsed);
                                } }, { children: _jsx(ExpandCollapseIcon, { bottom: toolbarOnBottom, collapsed: collapsed }) })) })), showFloatingNotification && (_jsx(NotificationList, { ariaLabel: (_f = props.floatingNotificationListProps) === null || _f === void 0 ? void 0 : _f.ariaLabel, items: floatingNotificationItems, shadowEffect: true }))] })) }))] }));
};
